import { useState, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react'

// Cubic bezier solver — maps progress t ∈ [0,1] → eased value
function cubicBezier(p1x, p1y, p2x, p2y) {
  return function (x) {
    if (x <= 0) return 0
    if (x >= 1) return 1
    let t = x
    for (let i = 0; i < 8; i++) {
      const bx = 3 * (1 - t) * (1 - t) * t * p1x + 3 * (1 - t) * t * t * p2x + t * t * t - x
      const dx = 3 * (1 - t) * (1 - t) * p1x + 6 * (1 - t) * t * (p2x - p1x) + 3 * t * t * (1 - p2x)
      if (Math.abs(dx) < 1e-10) break
      t -= bx / dx
      t = Math.max(0, Math.min(1, t))
    }
    return 3 * (1 - t) * (1 - t) * t * p1y + 3 * (1 - t) * t * t * p2y + t * t * t
  }
}

const EASING_FNS = {
  'linear': (t) => t,
  'ease': cubicBezier(0.25, 0.1, 0.25, 1.0),
  'ease-in': cubicBezier(0.42, 0, 1.0, 1.0),
  'ease-out': cubicBezier(0, 0, 0.58, 1.0),
  'ease-in-out': cubicBezier(0.42, 0, 0.58, 1.0),
  'cubic-bezier(0.34, 1.56, 0.64, 1)': cubicBezier(0.34, 1.56, 0.64, 1),
}

const CircleProgress = forwardRef(function CircleProgress({
  size,
  strokeWidth,
  fadeDuration = 1.5,
  easing = 'ease-in-out',
  duration = 500,
  progress = 0,
  children,
}, ref) {
  const [animatedProgress, setAnimatedProgress] = useState(progress)
  const currentValue = useRef(progress)
  const animFrame = useRef(null)

  useEffect(() => {
    return () => {
      if (animFrame.current) cancelAnimationFrame(animFrame.current)
    }
  }, [])

  const animate = useCallback((from, to) => {
    return new Promise((resolve) => {
      if (animFrame.current) cancelAnimationFrame(animFrame.current)
      if (Math.abs(to - from) < 0.5) {
        setAnimatedProgress(to)
        currentValue.current = to
        resolve()
        return
      }
      const easeFn = EASING_FNS[easing] || EASING_FNS['ease-in-out']
      const startTime = performance.now()

      function tick(now) {
        const t = Math.min((now - startTime) / duration, 1)
        const val = Math.max(from + (to - from) * easeFn(t), 0)
        setAnimatedProgress(val)
        currentValue.current = val
        if (t < 1) {
          animFrame.current = requestAnimationFrame(tick)
        } else {
          animFrame.current = null
          resolve()
        }
      }

      animFrame.current = requestAnimationFrame(tick)
    })
  }, [duration, easing])

  useImperativeHandle(ref, () => ({
    /**
     * Animate to a target progress percentage.
     * @param {number} targetPercent - Target progress (e.g. 75 for 75%)
     * @returns {Promise<void>} Resolves when animation completes
     */
    updateProgress(targetPercent) {
      const current = currentValue.current
      const currentLoop = Math.floor(current / 100)
      const currentPosInLoop = current - currentLoop * 100
      let effectiveTarget
      if (currentPosInLoop < 2) {
        // Near loop start: stay in current loop, animate to target (min 0.1 to avoid full-ring special case)
        effectiveTarget = currentLoop * 100 + Math.max(targetPercent, 0.1)
      } else if (targetPercent >= currentPosInLoop) {
        effectiveTarget = currentLoop * 100 + targetPercent
      } else {
        // Target is behind current position — advance to next loop
        // Use 0.1 minimum to avoid landing on an exact loop boundary (which triggers the full-ring special case)
        effectiveTarget = (currentLoop + 1) * 100 + Math.max(targetPercent, 0.1)
      }
      return animate(current, Math.max(0, effectiveTarget))
    },
  }), [animate])

  const displayProgress = animatedProgress
  const totalAngle = (Math.max(0, displayProgress) / 100) * 360

  const innerR = size / 2 - strokeWidth
  const cycleCount = Math.floor(totalAngle / 360)
  let currentArc = totalAngle % 360
  if (currentArc < 0.01 && totalAngle > 0) currentArc = 360

  const midR = (size / 2 + innerR) / 2
  const circumference = 2 * Math.PI * midR
  const minArc = 0.5
  const capAngle = Math.atan2(strokeWidth / 2, midR) * (180 / Math.PI)
  const isFullCycle = currentArc >= 360

  // Normal arc mask — visually caps at 96% (345.6°), head cap handles the rest
  const maxVisualArc = 345.6
  const maskArc = Math.max(Math.min(currentArc, maxVisualArc), minArc)
  const arcLength = circumference * (maskArc / 360)

  const arcMask = `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'>` +
    `<circle cx='${(size / 2)}' cy='${(size / 2)}' r='${midR}' fill='none' stroke='white' stroke-width='${strokeWidth}' stroke-linecap='round' stroke-dasharray='${arcLength} ${circumference}' transform='rotate(90 ${size / 2} ${size / 2})'/>` +
    `</svg>`
  )}")`

  // Gradient spans full 360° with fixed stops, rotates so #BDFF2D is always at the head
  const gradArc = Math.max(Math.min(currentArc, maxVisualArc), minArc)
  const mainGradient = `conic-gradient(from ${180 + gradArc + capAngle}deg, #00B931 0deg, #BDFF2D 360deg)`
  const fullCircleGradient = `conic-gradient(from 180deg, #00B931 0deg, #BDFF2D 360deg)`

  // Head position — follows actual progress, not capped arc
  const headPosDeg = isFullCycle ? 360 : Math.min(currentArc, 360)
  const headDeg = 180 + headPosDeg
  const headRad = (headDeg * Math.PI) / 180
  const headCapSize = strokeWidth
  const headX = size / 2 + midR * Math.sin(headRad) - headCapSize / 2
  const headY = size / 2 - midR * Math.cos(headRad) - headCapSize / 2

  const headColor = '#BDFF2D'

  // Tail position
  const tailX = size / 2 + midR * Math.sin(Math.PI) - strokeWidth / 2
  const tailY = size / 2 - midR * Math.cos(Math.PI) - strokeWidth / 2

  return (
    <div className="circle-container" style={{ width: size, height: size, '--inner-r': `${innerR}px` }}>
      {/* Dark center */}
      <div className="circle-center" style={{ width: innerR * 2 + 2, height: innerR * 2 + 2 }} />

      {/* Track */}
      <div className="ring track" />

      {/* Ghost — fades out each time a cycle completes */}
      {cycleCount > 0 && (
        <div
          key={`ghost-${cycleCount}`}
          className="ring ghost"
          style={{
            '--inner-r': `${innerR}px`,
            background: fullCircleGradient,
            animationDuration: `${fadeDuration}s`,
          }}
        />
      )}

      {/* Main arc */}
      { (
        <div
          className="arc-rounded"
          style={{
            background: mainGradient,
            WebkitMaskImage: arcMask,
            WebkitMaskSize: '100% 100%',
            maskImage: arcMask,
            maskSize: '100% 100%',
            zIndex: 2,
          }}
        />
      )}

      {/* Head shadow */}
      {totalAngle > 0 && (
        <div
          style={{
            position: 'absolute',
            left: headX,
            top: headY,
            width: headCapSize,
            height: headCapSize,
            borderRadius: '50%',
            background: '#BDFF2D',
            zIndex: 2,
          }}
        />
      )}

      {/* Tail cap — green circle to cover gradient bleed at high % */}
      <div
        style={{
          position: 'absolute',
          left: tailX,
          top: tailY,
          width: strokeWidth,
          height: strokeWidth,
          borderRadius: '50%',
          background: '#00B931',
          opacity: Math.min(Math.max((currentArc / 3.6 - 94) / 1, 0), 1),
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* Bridge arc — extends from visual arc end (96%) to actual head */}
      {totalAngle > 0 && currentArc > maxVisualArc && (() => {
        const bridgeDeg = currentArc - maxVisualArc
        const bridgeLen = circumference * (bridgeDeg / 360)
        const bridgeMask = `url("data:image/svg+xml,${encodeURIComponent(
          `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'>` +
          `<circle cx='${size / 2}' cy='${size / 2}' r='${midR}' fill='none' stroke='white' stroke-width='${strokeWidth}' stroke-linecap='round' stroke-dasharray='${bridgeLen} ${circumference}' transform='rotate(${90 + maxVisualArc} ${size / 2} ${size / 2})'/>` +
          `</svg>`
        )}")`
        return (
          <div
            className="arc-rounded"
            style={{
              background: headColor,
              WebkitMaskImage: bridgeMask,
              WebkitMaskSize: '100% 100%',
              maskImage: bridgeMask,
              maskSize: '100% 100%',
              zIndex: 3,
            }}
          />
        )
      })()}

      <div className="center-label">
        {children ?? `${Math.round(displayProgress)}%`}
      </div>
    </div>
  )
})

export default CircleProgress
